// Timezone utilities for PrepLetter using luxon
import { DateTime } from "luxon";

/**
 * Convert a given hour in a selected timezone to its UTC hour using luxon
 * @param {number} localHour - Hour in the local timezone (0-23)
 * @param {string} timezone - The timezone string (e.g., "America/New_York")
 * @returns {number} - The equivalent hour in UTC
 */
export const convertToUTCHour = (localHour, timezone) => {
  try {
    // Get today's date in the selected timezone, set hour
    const dt = DateTime.now()
      .setZone(timezone)
      .set({ hour: localHour, minute: 0, second: 0, millisecond: 0 });
    // Convert to UTC and extract the hour
    return dt.toUTC().hour;
  } catch (error) {
    console.error("Error converting timezone with luxon:", error);
    return localHour;
  }
};

/**
 * Format hour for display with AM/PM
 * @param {number} hour - Hour in 24-hour format (0-23)
 * @returns {string} - Formatted hour string (e.g., "3 PM (15:00)")
 */
export const formatHourForDisplay = (hour) => {
  const ampm = hour >= 12 ? "PM" : "AM";
  const hour12 = hour % 12 || 12; // Convert 0 to 12 for 12 AM
  return `${hour12} ${ampm} (${hour.toString().padStart(2, "0")}:00)`;
};

/**
 * Get a readable timezone label from IANA timezone string
 * @param {string} timezone
 * @returns {string}
 */
export const getTimezoneLabel = (timezone) => {
  if (!timezone) return "";
  const dt = DateTime.now().setZone(timezone);
  // e.g. "America/New_York" => "New York (GMT-4)"
  const parts = timezone.split("/");
  const city = parts[1]?.replace(/_/g, " ") || timezone;
  const offset = dt.toFormat("ZZ");
  return `${city} (GMT${offset})`;
};

/**
 * Get all valid IANA timezones supported by the browser
 * Uses modern Intl.supportedValuesOf API with fallback to curated list
 * @returns {string[]}
 */
export const getAllTimezones = () => {
  // Use Intl.supportedValuesOf('timeZone') if supported, fallback to hardcoded list
  if (typeof Intl.supportedValuesOf === "function") {
    try {
      const supportedTimezones = Intl.supportedValuesOf("timeZone");
      if (supportedTimezones && supportedTimezones.length > 0) {
        // Sort timezones by GMT offset for better UX
        const sortedTimezones = supportedTimezones
          .filter((tz) => tz !== "UTC")
          .sort((a, b) => {
            try {
              const offsetA = DateTime.now().setZone(a).offset;
              const offsetB = DateTime.now().setZone(b).offset;
              // Sort by offset first, then alphabetically within same offset
              if (offsetA !== offsetB) {
                return offsetA - offsetB;
              }
              return a.localeCompare(b);
            } catch (error) {
              // Fallback to alphabetical if timezone is invalid
              return a.localeCompare(b);
            }
          });

        return ["UTC", ...sortedTimezones];
      }
    } catch (error) {
      console.warn("Error getting supported timezones from Intl API:", error);
    }
  }

  // Fallback to curated list for older browsers (sorted by GMT offset)
  const fallbackTimezones = [
    "America/Honolulu", // GMT-10
    "America/Anchorage", // GMT-9
    "America/Los_Angeles", // GMT-8
    "America/Denver", // GMT-7
    "America/Chicago", // GMT-6
    "America/New_York", // GMT-5
    "America/Santiago", // GMT-4
    "America/Sao_Paulo", // GMT-3
    "America/Buenos_Aires", // GMT-3
    "UTC", // GMT+0
    "Europe/London", // GMT+0
    "Europe/Dublin", // GMT+0
    "Europe/Paris", // GMT+1
    "Europe/Berlin", // GMT+1
    "Europe/Madrid", // GMT+1
    "Europe/Rome", // GMT+1
    "Europe/Amsterdam", // GMT+1
    "Europe/Brussels", // GMT+1
    "Europe/Zurich", // GMT+1
    "Europe/Stockholm", // GMT+1
    "Africa/Cairo", // GMT+2
    "Africa/Johannesburg", // GMT+2
    "Europe/Istanbul", // GMT+3
    "Europe/Moscow", // GMT+3
    "Asia/Riyadh", // GMT+3
    "Asia/Dubai", // GMT+4
    "Asia/Kolkata", // GMT+5:30
    "Asia/Dhaka", // GMT+6
    "Asia/Bangkok", // GMT+7
    "Asia/Singapore", // GMT+8
    "Asia/Shanghai", // GMT+8
    "Asia/Hong_Kong", // GMT+8
    "Asia/Taipei", // GMT+8
    "Australia/Perth", // GMT+8
    "Asia/Seoul", // GMT+9
    "Asia/Tokyo", // GMT+9
    "Australia/Adelaide", // GMT+9:30
    "Australia/Sydney", // GMT+10
    "Australia/Brisbane", // GMT+10
    "Pacific/Auckland", // GMT+12
    "America/Toronto", // GMT-5
    "America/Vancouver", // GMT-8
    "America/Mexico_City", // GMT-6
    "America/Bogota", // GMT-5
    "Asia/Jerusalem", // GMT+2
  ];

  // Sort the fallback list by GMT offset too
  const sortedFallback = fallbackTimezones.sort((a, b) => {
    try {
      const offsetA = DateTime.now().setZone(a).offset;
      const offsetB = DateTime.now().setZone(b).offset;
      if (offsetA !== offsetB) {
        return offsetA - offsetB;
      }
      return a.localeCompare(b);
    } catch (error) {
      return a.localeCompare(b);
    }
  });

  return sortedFallback;
};

/**
 * Get the user's local timezone (IANA string)
 * @returns {string}
 */
export const getUserLocalTimezone = () => {
  try {
    return DateTime.local().zoneName || "UTC";
  } catch (error) {
    console.error("Error getting local timezone:", error);
    return "UTC";
  }
};
